import { useCallback } from 'react';
import { useLocalStorageState } from '@reflex/ui/hooks/useWebStorageState';

/**
 * How the expanded sidebar presents its section groups (Plugins, Misc, ...).
 * Purely a client-side presentation preference: every style renders the same
 * permission-filtered nav sources, so switching styles never reveals or
 * hides a destination.
 *
 * - `drill`: sections slide in as focused panels (the original behavior).
 * - `expand`: sections expand in place as collapsible trees.
 * - `flat`: every section is always expanded in one scrolling list.
 * - `flyout`: sections open in a floating panel beside the sidebar.
 * - `sessions`: the session list is the sidebar (phase 2 of the chat-first
 *   shell rework); page links collapse into a footer strip.
 */
export const NAV_STYLES = ['drill', 'expand', 'flat', 'flyout', 'sessions'] as const;
export type NavStyle = (typeof NAV_STYLES)[number];

/**
 * `expand` is the default: sections open in place as collapsible trees, so
 * every destination stays reachable in a single scrolling column without the
 * back-and-forth of the drill panels. Users who prefer the sliding panels can
 * still switch back to `drill` on the settings page. (Mobile is unaffected —
 * the bottom-sheet nav has its own drill presentation and ignores this value.)
 */
// The sessions dock is the chat-first shell's default: the session list is
// the sidebar. The other styles remain in the picker as escape hatches
// until the shell rework retires them.
export const DEFAULT_NAV_STYLE: NavStyle = 'sessions';

const STORAGE_KEY = 'nav-style';

export interface NavStyleOption {
  value: NavStyle;
  label: string;
  description: string;
}

/** Display metadata for the settings picker, in presentation order. */
export const NAV_STYLE_OPTIONS: readonly NavStyleOption[] = [
  {
    value: 'drill',
    label: 'Drill in',
    description: 'Sections slide in as focused panels.',
  },
  {
    value: 'expand',
    label: 'Expand',
    description: 'Sections expand in place as trees.',
  },
  {
    value: 'flat',
    label: 'Flat list',
    description: 'Every link visible in one scrolling list.',
  },
  {
    value: 'flyout',
    label: 'Flyout',
    description: 'Sections open in a floating panel.',
  },
  {
    value: 'sessions',
    label: 'Sessions',
    description: 'Your sessions become the sidebar; pages move to a footer strip.',
  },
];

/**
 * Parse a persisted value back into a `NavStyle`. localStorage is an
 * untrusted boundary (stale keys from older builds, manual edits), so an
 * unknown value falls back to the default instead of leaking through.
 */
export function parseNavStyle(value: unknown): NavStyle {
  return NAV_STYLES.includes(value as NavStyle) ? (value as NavStyle) : DEFAULT_NAV_STYLE;
}

/**
 * The user's sidebar navigation style, persisted per browser (same
 * mechanism as the sidebar width). Reactive across components and tabs via
 * `useLocalStorageState`, so the sidebar restyles immediately when the
 * setting changes on the settings page.
 */
export function useNavStyle(): [NavStyle, (style: NavStyle) => void] {
  const [raw, setRaw] = useLocalStorageState<string>(STORAGE_KEY, DEFAULT_NAV_STYLE);
  const setStyle = useCallback((style: NavStyle) => setRaw(style), [setRaw]);
  return [parseNavStyle(raw), setStyle];
}
